/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Alert,
  Button,
  Cell,
  Grid,
  Heading,
  HFlow,
  Icon,
  isEmpty,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
  Tag,
  Text,
  VFlow,
} from 'bold-ui'
import { AccordionCompositeItem } from 'components/accordion/AccordionCompositeItem'
import { useAlert } from 'components/alert'
import useSession from 'components/auth/useSession'
import { resolveName } from 'components/form/final-form/useField'
import { confirm } from 'components/modals/confirm'
import { ButtonLink, PrivateRoute } from 'components/route'
import { TitleGroup } from 'components/TitleGroup'
import theme from 'config/theme'
import moment from 'cypress/node_modules/moment'
import { useMedicamentoUsoModalQuery } from 'graphql/hooks.generated'
import { ReceitaMedicamento, TipoReceitaEnum, UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import { useCallback, useMemo, useState } from 'react'
import { Accordion } from 'react-accessible-accordion'
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays'
import { Switch, useHistory, useRouteMatch } from 'react-router'
import { tipoReceitaRecord } from 'types/enums'
import { metaPath } from 'util/metaPath'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'
import { grupoCboPrescricao } from 'view/atendimentos/detail/soap/plano/acessos'
import { periodoTratamento } from 'view/atendimentos/detail/soap/plano/prescricao-medicamento/components/PeriodoTratamentoSelectField'

import InterromperTratamentoModal, { MedicamentoInterromperFormModel } from './InterromperTratamentoModal'
import MedicamentosConcluidosTable from './MedicamentosConcluidosTable'

interface MedicamentosAtivosConcluidosModalProps {
  prontuarioId: ID
  dataAtendimento: Instant
}

const path = metaPath<SoapState>()

export default function MedicamentosAtivosConcluidosModal(props: MedicamentosAtivosConcluidosModalProps) {
  const { prontuarioId, dataAtendimento } = props
  const alert = useAlert()
  const history = useHistory()
  const match = useRouteMatch()
  const [medicamentoToInterrupt, setMedicamentoToInterrupt] = useState<ReceitaMedicamento>(undefined)
  const { hasCboAuth } = useSession({ fetchPolicy: 'cache-only' })

  const {
    data: { listaMedicamento: listaMedicamentosAtivos },
    loading: loadingAtivos,
  } = useMedicamentoUsoModalQuery({
    variables: {
      input: { prontuarioId, dataAtendimento, usoContinuo: false, ativo: true },
    },
  })

  const onClearItem = () => {
    setMedicamentoToInterrupt(undefined)
  }

  const onClose = useCallback(() => {
    return history.push(match.url.replace('/medicamentos-ativos-concluidos', ''))
  }, [history, match.url])

  const renderItemHeader = useCallback(
    (interrompidosAgora?: MedicamentoInterromperFormModel[]) => (receita: ReceitaMedicamento) => {
      return (
        <HFlow
          justifyContent='space-between'
          style={css`
            width: 100%;
          `}
        >
          <VFlow vSpacing={0.2}>
            <div
              css={css`
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
              `}
            >
              <Heading level={5}>
                {`${receita.medicamento.principioAtivo} ${receita.medicamento.concentracao ?? ''}`.trim()}
              </Heading>
              {interrompidosAgora?.find((i) => i.id === receita.id) && (
                <div css={styles.interrompidoAgoraStyles}>
                  <Icon
                    icon='clockOutline'
                    size={1}
                    style={css`
                      margin-right: 0.5rem;
                    `}
                    data-testid='MedicamentoAtivosConcluidos.InterrompidoAgora'
                  />
                  Interrompido agora
                </div>
              )}
            </div>
            <div>
              {receita.usoContinuo && (
                <Tag
                  type='normal'
                  style={css`
                    margin-right: 0.5rem;
                  `}
                >
                  Uso contínuo
                </Tag>
              )}
              {receita.tipoReceita !== TipoReceitaEnum.COMUM && (
                <Tag
                  type='info'
                  style={css`
                    margin-right: 0.5rem;
                  `}
                >
                  {tipoReceitaRecord[receita.tipoReceita]}
                </Tag>
              )}
              {receita.tratamentoInterrompido && (
                <Tag type='normal' data-testid='MedicamentoAtivosConcluidos.TratamentoInterrompido'>
                  Tratamento interrompido
                </Tag>
              )}
            </div>
            <Text>{receita.posologia}</Text>
          </VFlow>
          <HFlow
            hSpacing={1}
            style={css`
              width: 100%;
              margin-right: 1rem;
            `}
            justifyContent='space-between'
          >
            <HFlow hSpacing={0.5} alignItems='center'>
              <Icon icon='calendarFilled' fill='primary' size={1} />
              <VFlow vSpacing={0.2}>
                <Text>
                  De
                  <Text fontWeight='bold'> {moment(receita.dtInicioTratamento).format('DD/MM/YYYY')}</Text>
                </Text>
                <Text>
                  até
                  <Text fontWeight='bold'>
                    {receita.atendProf?.versaoAnterior
                      ? ` ${periodoTratamento[receita.medidaTempoTratamento]}`
                      : receita.medidaTempoTratamento !== UnidadeMedidaTempoEnum.INDETERMINADO &&
                        receita.dtFimTratamento
                      ? ` ${moment(receita.dtFimTratamento).format('DD/MM/YYYY')}`
                      : ` ${periodoTratamento[receita.medidaTempoTratamento]}`}
                  </Text>
                </Text>
              </VFlow>
            </HFlow>
          </HFlow>
        </HFlow>
      )
    },
    []
  )

  const renderItemBody = useCallback((receita: ReceitaMedicamento) => {
    return (
      <VFlow
        style={css`
          padding: 1rem;
        `}
      >
        {receita.tratamentoInterrompido && (
          <VFlow vSpacing={0}>
            <Text fontWeight='bold'>Tratamento interrompido</Text>
            {receita.dtInterrupcao && (
              <Text data-testid='MedicamentoAtivosConcluidos.dtInterrupcao'>{`${moment(receita.dtInterrupcao).format(
                'DD/MM/YYYY'
              )}`}</Text>
            )}
            {receita.justificativaInterrupcao && (
              <Text data-testid='MedicamentoAtivosConcluidos.JustificativaInterrompido'>
                {receita.justificativaInterrupcao}
              </Text>
            )}
          </VFlow>
        )}
        <VFlow vSpacing={0}>
          <Text fontWeight='bold'>Recomendações</Text>
          {receita.recomendacoes ? (
            <Text
              style={css`
                white-space: pre-wrap;
                word-break: break-all;
              `}
            >
              {receita.recomendacoes}
            </Text>
          ) : (
            <Text fontStyle='italic'>Não há recomendações para o medicamento</Text>
          )}
        </VFlow>
      </VFlow>
    )
  }, [])

  const noResultMedicamentoEmUso = isEmpty(listaMedicamentosAtivos?.content) && 'Nenhum medicamento em uso'

  const renderFieldArray = useCallback(
    (fieldArrayRenderProps: FieldArrayRenderProps<MedicamentoInterromperFormModel, any>) => {
      const {
        fields: { push, remove, value },
      } = fieldArrayRenderProps

      const handleOnSubmit = (interrupcao: MedicamentoInterromperFormModel) => {
        push(interrupcao)
        alert('success', 'Tratamento interrompido com sucesso.')
      }

      const handleOnCancel = (interrupcao: MedicamentoInterromperFormModel) => {
        const item = value?.find((item) => item.id === interrupcao.id)
        if (item) {
          const index = value.indexOf(item)
          confirm({
            title: 'Deseja cancelar a interrupção do tratamento?',
            confirmLabel: 'Confirmar',
            cancelLabel: 'Cancelar',
            depthLevel: 5,
            manageOverflow: false,
            onConfirm: () => {
              remove(index)
              onClearItem()
              alert('success', 'Interrupção cancelada com sucesso.')
            },
          })()
        }
      }

      const renderItemButton = (receita: ReceitaMedicamento) => {
        const item = value?.find((item) => item.id === receita.id)
        return (
          <div
            css={css`
              width: 12rem;
            `}
          >
            {item ? (
              <Button
                size='small'
                title='Cancelar interrupção'
                onClick={() => handleOnCancel(item)}
                data-testid='MedicamentoAtivosConcluidos.BotaoCancelarInterrupcao'
              >
                Cancelar interrupção
              </Button>
            ) : (
              <ButtonLink
                size='small'
                to={`${match.url}/interromper-tratamento`}
                onClick={() => setMedicamentoToInterrupt(receita)}
                data-testid='MedicamentoAtivosConcluidos.InterromperButton'
              >
                Interromper tratamento
              </ButtonLink>
            )}
          </div>
        )
      }

      return (
        <div>
          <Accordion allowZeroExpanded={true} allowMultipleExpanded={true}>
            <VFlow vSpacing={0.5}>
              {loadingAtivos && (
                <div css={styles.loadingContainer}>
                  <Spinner />
                  {'Carregando resultados'}
                </div>
              )}
              {!loadingAtivos && isEmpty(listaMedicamentosAtivos?.content) && (
                <Text
                  style={css`
                    color: ${theme.pallete.text.secondary};
                    font-style: italic;
                    text-align: center;
                  `}
                >
                  {noResultMedicamentoEmUso}
                </Text>
              )}
              {listaMedicamentosAtivos?.content?.map((item: ReceitaMedicamento) => (
                <AccordionCompositeItem
                  key={item.id}
                  header={renderItemHeader(value)(item)}
                  headerButtons={hasCboAuth(grupoCboPrescricao.adicionar) && renderItemButton(item)}
                  body={renderItemBody(item)}
                />
              ))}
            </VFlow>
          </Accordion>
          <Switch>
            <PrivateRoute
              path={`${match.url}/interromper-tratamento`}
              permission={grupoCboPrescricao.interromperTratamento}
            >
              <InterromperTratamentoModal
                receita={medicamentoToInterrupt}
                onSubmit={handleOnSubmit}
                onClear={onClearItem}
              />
            </PrivateRoute>
          </Switch>
        </div>
      )
    },
    [
      alert,
      hasCboAuth,
      listaMedicamentosAtivos,
      loadingAtivos,
      match.url,
      medicamentoToInterrupt,
      noResultMedicamentoEmUso,
      renderItemBody,
      renderItemHeader,
    ]
  )

  return useMemo(
    () => (
      <Modal open={true} onClose={onClose}>
        <ModalBody>
          <TitleGroup
            title='Medicamentos em uso/tratamento concluído'
            style={css`
              margin-bottom: 1rem;
            `}
          />
          <Grid alignItems='flex-start' gap={0}>
            <Cell size={12}>
              <Alert type='info' inline>
                Ao interromper o tratamento, a medicação será apresentada com tratamento concluído no próximo
                atendimento.
              </Alert>
            </Cell>
            <Cell size={12}>
              <Heading level={2}>Medicamentos em uso</Heading>
              <FieldArray<MedicamentoInterromperFormModel>
                name={resolveName(path.medicamentosInterromper)}
                render={renderFieldArray}
              />
            </Cell>
            <Cell size={12}>
              <Heading
                level={2}
                style={css`
                  margin-bottom: 0.5rem;
                `}
              >
                Medicamentos com tratamento concluído
              </Heading>
              <MedicamentosConcluidosTable
                prontuarioId={prontuarioId}
                dataAtendimento={dataAtendimento}
                accordionItemHeader={renderItemHeader()}
                accordionItemBody={renderItemBody}
              />
            </Cell>
          </Grid>
        </ModalBody>
        <ModalFooter style={styles.footer}>
          <HFlow justifyContent='flex-end'>
            <Button onClick={onClose} style={styles.footerButton}>
              Fechar
            </Button>
          </HFlow>
        </ModalFooter>
      </Modal>
    ),
    [dataAtendimento, onClose, prontuarioId, renderFieldArray, renderItemBody, renderItemHeader]
  )
}

const styles = {
  footer: css`
    border-top: 1px solid + ${theme.pallete.gray.c80};
  `,
  footerButton: css`
    width: 9rem;
    height: 3rem;
  `,
  interrompidoAgoraStyles: css`
    color: ${theme.pallete.primary.main};
    display: inline-flex;
    margin-left: 0.5rem;
    align-items: center;
    font-weight: normal;
  `,
  loadingContainer: css`
    color: ${theme.pallete.primary.main};
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    & > *:first-of-type {
      margin-right: 0.5rem;
    }
  `,
}
